import {useEffect, useRef, useState} from 'react';
import {STATUSES} from '@chekin/core';

const CURVE_MS = 30_000;
const CURVE_CEILING = 96;
const CURVE_EXPONENT = 1.35;
const TAIL_STEP_MS = 5_000;
const MAX_ESTIMATE = 99;
const SETTLE_MS = 600;
const TICK_MS = 100;

function estimateProgress(elapsedMs: number) {
  if (elapsedMs <= 0) return 0;
  if (elapsedMs < CURVE_MS) {
    return CURVE_CEILING * (1 - Math.pow(1 - elapsedMs / CURVE_MS, CURVE_EXPONENT));
  }
  return Math.min(MAX_ESTIMATE, CURVE_CEILING + (elapsedMs - CURVE_MS) / TAIL_STEP_MS);
}

function settleProgress(from: number, elapsedMs: number) {
  const t = Math.min(1, elapsedMs / SETTLE_MS);
  const eased = 1 - (1 - t) * (1 - t);
  return from + (100 - from) * eased;
}

function useEstimatedProgress(state: STATUSES) {
  const [progress, setProgress] = useState(0);
  const progressRef = useRef(0);
  const isLoading = state === STATUSES.loading;
  const isSettled = state === STATUSES.success || state === STATUSES.error;

  useEffect(() => {
    if (!isLoading && !isSettled) return;

    const startedAt = Date.now();
    const from = progressRef.current;
    const tick = () => {
      const elapsed = Date.now() - startedAt;
      const next = isLoading ? estimateProgress(elapsed) : settleProgress(from, elapsed);
      progressRef.current = next;
      setProgress(Math.round(next));
      return !isLoading && elapsed >= SETTLE_MS;
    };

    tick();
    const interval = setInterval(() => {
      if (tick()) clearInterval(interval);
    }, TICK_MS);
    return () => clearInterval(interval);
  }, [isLoading, isSettled]);

  return progress;
}

export {MAX_ESTIMATE, estimateProgress, useEstimatedProgress};
